<template>
  <a-select
    ref="aSelectRef"
    v-model:popup-visible="popupVisible"
    v-model:input-value="searchKey"
    :model-value="activeOption"
    placeholder="请选择"
    :allow-clear="allowClear"
    show-header-on-empty
    show-footer-on-empty
    unmount-on-close
    :loading="loading"
    :filter-option="!showCustomSearchInput"
    :value-key="valueField"
    :fallback-option="fallback"
    :show-extra-options="false"
    :allow-search="allowSearch || isTable"
    :trigger-props="triggerPropsConfig"
    v-bind="$attrs"
    @search="handleSearch"
    @change="selectChange"
    @clear="clearHandler"
    @dropdown-reach-bottom="dropdownReachBottom"
    @popup-visible-change="visibleChange"
    @keydown="handleSelectKeyDown"
    @mouseenter="handleMouse($event, 'mouseenter')"
    @mouseleave="handleMouse($event, 'mouseleave')"
  >
    <template v-if="isTable" #empty>
      <BasisTable
        ref="basisTableRef"
        radio
        :radio-config="radioConfig"
        :loading="tableLoading"
        :data="selectOptions"
        :columns="columns"
        max-height="500"
        class="max-w-670"
        :row-config="{ isHover: true, isCurrent: true, keyField: valueField }"
        @radio-change="radioChange"
        @sort-change="sortChange"
        @keydown="handleSelectKeyDown($event.$event)"
      >
      </BasisTable>
    </template>
    <template v-if="!isTable">
      <a-option
        v-for="item in selectOptions"
        :key="item[valueField]"
        class="select-options"
        :value="item"
        :label="item[labelField]"
        :disabled="
          showDisabled && (item[disabledField] === 0 || item.customDisabled)
        "
      >
        <slot name="custom-option" :item="item"></slot>
      </a-option>
    </template>
    <template v-if="showCustomSearchInput" #header>
      <div class="relative">
        <div style="padding: 6px 12px">
          <a-input
            ref="searchInputRef"
            v-model="searchKey"
            placeholder="输入查询"
            allow-clear
            @clear="handleSearch()"
            @input="handleSearch"
            @keydown="handleInputKeyDown"
          />
        </div>
        <div v-if="hasActionSlot" style="padding: 6px 12px">
          <slot name="action"></slot>
        </div>
        <div v-if="showTab" class="tab-box">
          <a-tabs position="left" hide-content @change="tabChange">
            <a-tab-pane
              v-for="item in tabList"
              :key="item.value"
              :title="item.text"
            ></a-tab-pane>
          </a-tabs>
        </div>
      </div>
    </template>
    <template v-if="!hideFooter" #footer>
      <div
        class="flex-center outline-none"
        tabindex="0"
        @keydown="handleSelectFooterKeyDown"
      >
        <div style="padding: 6px 0; text-align: center">
          <a-button
            v-if="addRouterName"
            type="text"
            @click="routerTo(addRouterName)"
            >新增</a-button
          >
          <a-button type="text" @click="refresh">刷新</a-button>
          <slot v-if="hasSwitchViewSlot" name="switch-view" />
        </div>
        <Pager
          v-if="isTable"
          class="flex-1"
          :pager="pageParams"
          pager-tips-text="上一页：PageUp，下一页：PageDown"
          :page-sizes="[10, 20]"
          @page-change="pageChange"
        />
      </div>
    </template>
    <template v-if="hasLabelSlot || isTable" #label="{ data }">
      <slot name="label" :data="data">
        <EllipsisTooltip :content="data.label" :line="1" />
      </slot>
    </template>
    <template v-if="hasArrowSlot || isTable" #arrow-icon>
      <slot name="arrow-icon">
        <icon-more />
      </slot>
    </template>
  </a-select>
</template>

<script lang="ts" setup>
  import {
    ref,
    watch,
    PropType,
    computed,
    getCurrentInstance,
    nextTick,
    reactive,
  } from 'vue';
  import { openNewWindow, routerPush } from '@/utils/router';
  import { cloneDeep, debounce, isBoolean, isFunction } from 'lodash';
  import { isPromise } from '@/utils/is';
  import BasisTable from '@/components/BasisTable/index.vue';
  import Pager from '@/components/BasisTable/pager.vue';
  import useTableKeyDown from '@/hooks/useTableKeyDown';
  import {
    handleSelectEllipsisTooltipMouse,
    focusSelect,
  } from '@/components/QsSelect/common';

  const instance = getCurrentInstance();

  const props = defineProps({
    modelValue: {
      type: [Number, String],
      // required: true,
    },
    // 选中的整个项目，用于获取当前选中，不能用于动态设置QsSelect的选中
    selectItem: {
      type: Object as PropType<Record<string, any>>,
      default: () => {
        return {};
      },
    },
    optionList: {
      type: Array as PropType<Record<string, any>[]>,
      default: () => [],
    },
    // table模式是的columns
    columns: {
      type: Array as PropType<Recordable[]>,
      default: () => [],
    },
    // table模式是的分页参数
    pageParams: {
      type: Object as PropType<Recordable>,
      default: () => {
        return {};
      },
    },
    triggerProps: {
      type: Object as PropType<Recordable>,
    },
    // 新增跳转的路由
    addRouterName: {
      type: String,
      default: '',
    },
    // 新增跳转路由的方式
    addRouterModel: {
      type: String,
      default: 'open',
      validator(value: string) {
        return ['open', 'push'].includes(value);
      },
    },
    // 新增不跳转
    addNoJump: {
      type: Boolean,
      default: false,
    },
    // 隐藏搜索
    hideHeader: {
      type: Boolean,
      default: false,
    },
    // 隐藏footer
    hideFooter: {
      type: Boolean,
      default: false,
    },
    // 根据id获取option的函数，用于默认值回显
    getOptionById: {
      type: Function,
      default: null,
    },
    // modelValue的字段名称
    valueField: {
      type: String,
      default: 'id',
    },
    // label的字段名称
    labelField: {
      type: String,
      default: 'name',
    },
    // disabled的字段名称
    disabledField: {
      type: String,
      default: 'state',
    },
    // 是否根据禁用字段名称判断禁用选项
    showDisabled: {
      type: Boolean,
      default: true,
    },
    allowClear: {
      type: Boolean,
      default: true,
    },
    allowSearch: {
      type: Boolean,
      default: false,
    },
    // 来自哪个组件
    source: {
      type: String,
      default: '',
    },
    // 回显默认值名称，当页面中同个select组件频繁调用getOptionById时建议传，减少网络请求
    echoName: {
      type: String,
      default: '',
    },
    // 用于回显验证的字段，参考选择仓库
    echoFields: {
      type: Object as PropType<Recordable>,
      default: () => ({}),
    },
    checkEcho: {
      type: Function,
    },
    // 是否显示成table模式
    isTable: {
      type: Boolean,
      default: false,
    },
    // 触发 change 事件前的回调函数。如果返回 false 则不会触发后续事件，也可使用 done 进行异步关闭。
    // isTable为true时待验证可行性
    onBeforeChange: {
      type: Function as PropType<
        (
          e: Recordable, // 当前选中值
          done: (closed: boolean) => void // 是否阻止选中的回调，也可在onBeforeChange中直接返回boolean值来阻止或放行
        ) => void | boolean | Promise<void | boolean> // 也可返回promise
      >,
    },
    // 清空时重置为0
    clearReturnZero: {
      type: Boolean,
    },
    // tab
    showTab: {
      type: Boolean,
    },
    tabList: {
      type: Array as PropType<Recordable[]>,
      default: () => [],
    },
  });
  const emit = defineEmits([
    'update:modelValue',
    'update:selectItem',
    'update:echoName',
    'search',
    'clear',
    'refresh',
    'load',
    'change',
    'pageChange',
    'sortChange',
    'tabChange',
    'addOption',
  ]);
  const aSelectRef = ref<any>();
  const searchInputRef = ref<any>();
  const popupVisible = ref<boolean>();
  const isRefresh = ref<boolean>(false);
  const activeOption = ref<Record<string, any>>();
  const selectOptions = computed(() => {
    return props.optionList;
  });
  watch(
    () => props.optionList,
    () => {
      loading.value = false;
      tableLoading.value = false;
      if (props.isTable && isRefresh.value) {
        isRefresh.value = false;
        basisTableRef.value?.refreshColumn();
      }
    }
  );
  const searchKey = ref('');
  const loading = ref<boolean>(false);
  const tableLoading = ref<boolean>(false);
  // 存首次回显的id和名称
  // const echoOption = ref<Recordable>({});

  const getOptionById = (id: number) => {
    const {
      valueField,
      labelField,
      echoName,
      checkEcho,
      echoFields = {},
    } = props;
    const option = props.optionList.find((e: any) => e[valueField] === id);
    if (option) {
      setActiveOption(option);
      return;
    }
    if (
      echoName &&
      id &&
      (!isFunction(checkEcho) || checkEcho({ id, name: echoName, echoFields }))
      // (!echoOption.value?.[valueField] || echoOption.value?.[valueField] === id)
    ) {
      // new: 有id和echoName就当成一个选项回显，不再去比对是否与旧的echoOption中id是否一致，否则在固定列的表格中会有重复的隐藏列，导致回显隐藏列时与echoOption不一致，会重复调接口校验
      // old：有回显昵称 && （回显选项中没值 || id一致），防止存在echoName后再在外部改变id时取错name
      const echoOptionTemp: Recordable = cloneDeep(echoFields);
      echoOptionTemp[valueField] = id;
      echoOptionTemp[labelField] = echoName;
      setActiveOption(echoOptionTemp);
      // echoOption.value = cloneDeep(echoOptionTemp);
      return;
    }
    if (id === 0) {
      if (activeOption?.value) {
        setActiveOption();
      }
      return;
    }
    if (!props.getOptionById) return;
    props.getOptionById(id)?.then((res: Record<string, any>) => {
      if (!res) return;
      if (res[valueField]?.toString() === id?.toString()) {
        setActiveOption(res);
      } else if (!res[valueField]) {
        // 根据这个id查不到则回显不了，所以change为空，避免有id没有name的显示问题
        emitChange();
      }
    });
  };
  // 重新打开时回显searchKey
  const searchKeyCopy = ref<string>('');
  const handleSearch = debounce((e?: any) => {
    searchKeyCopy.value = e;
    setLoading();
    emit('search', e ?? '');
  }, 500);

  const selectChange = async (val: any) => {
    const e = val || {}; // clear时val为空串
    if (!isFunction(props.onBeforeChange)) {
      emitChange(e);
      return Promise.resolve(true);
    }
    const closed = await new Promise<boolean>(
      // eslint-disable-next-line no-async-promise-executor
      async (resolve) => {
        let result = props.onBeforeChange?.(e ?? {}, (closed = true) =>
          resolve(closed)
        );
        if (isPromise(result) || !isBoolean(result)) {
          // promise || 无返回值（使用callback）
          setLoading();
        }
        if (isPromise(result)) {
          try {
            // if onBeforeChange is Promise<void> ,set Defaults true
            result = (await result) ?? true;
          } catch (error) {
            result = false;
          }
        }
        if (isBoolean(result)) {
          resolve(result);
        }
      }
    );

    if (closed) {
      // 放行change
      emitChange(e);
    } else {
      // 阻止选中
      const activeOptionTemp = cloneDeep(activeOption.value);
      activeOption.value = e;
      nextTick(() => {
        // 重新对activeOption赋值，否则a-select中会存在回显错误问题
        activeOption.value = activeOptionTemp;
        setPopupVisible(true);
      });
    }
    if (selectLoading.value) {
      // 取消loading
      setLoading(false);
    }
    return Promise.resolve(closed);
  };

  // change同步
  const emitChange = (e?: any) => {
    setActiveOption(e);
    emit(
      'update:modelValue',
      e?.[props.valueField] ?? (props.clearReturnZero ? 0 : '')
    );
    emit('change', e ?? {});
  };

  // table模式的单选事件
  const radioChange = async ({ row }: any) => {
    const result = await selectChange(row);
    if (result) {
      handleRadioRow({ row });
      setPopupVisible(false);
    }
  };

  // 设置下拉框显示隐藏
  const setPopupVisible = (visible: boolean) => {
    focusSelect(aSelectRef.value, visible);
  };

  // 更改activeOption由此处统一触发，并修改echoName
  const setActiveOption = (option?: Recordable) => {
    activeOption.value = option;
    emit('update:selectItem', option ?? {});
    emit('update:echoName', option?.[props.labelField] ?? '');
  };

  const clearHandler = () => {
    emit('clear');
    if (props.isTable) {
      clearSelectRow();
    }
  };

  const refresh = () => {
    setLoading();
    searchKey.value = '';
    emit('refresh', '');
    if (props.isTable) {
      isRefresh.value = true;
    }
  };

  // table模式的分页事件
  const pageChange = (e: { page: number; limit: number }) => {
    setLoading();
    emit('pageChange', e);
  };

  // table模式的排序事件
  const sortChange = (e: any) => {
    setLoading();
    emit('sortChange', e);
  };

  // tab 改变
  const tabChange = (e: any) => {
    setLoading();
    emit('tabChange', e);
  };

  const setLoading = (isLoading?: boolean) => {
    if (props.isTable) {
      setTableLoading(isLoading);
      return;
    }
    if (isBoolean(isLoading)) {
      loading.value = isLoading;
      return;
    }
    loading.value = true;
    const timer = setTimeout(() => {
      loading.value = false;
      clearTimeout(timer);
    }, 3000);
  };

  // 设置table模式的loading
  const setTableLoading = (isLoading?: boolean) => {
    if (isBoolean(isLoading)) {
      tableLoading.value = isLoading;
      return;
    }
    tableLoading.value = true;
    const timer = setTimeout(() => {
      tableLoading.value = false;
      clearTimeout(timer);
    }, 3000);
  };

  const dropdownReachBottom = () => {
    emit('load');
  };
  // 是否是第一次打开下拉选择框
  const firstTimeOpen = ref<boolean>(true);
  // 第一次打开时请求列表
  const visibleChange = (visible: boolean) => {
    if (visible) {
      nextTick(() => {
        if (props.isTable) {
          // 回显表格单选
          if (activeOption.value?.[props.valueField]) {
            handleRadioRow({ row: activeOption.value });
          }
        } else {
          searchInputRef.value?.focus();
        }
      });
      if (firstTimeOpen.value) {
        firstTimeOpen.value = false;
        refresh();
      } else {
        searchKey.value = searchKeyCopy.value || '';
      }
    } else {
      // 聚焦状态有高亮边框
      nextTick(() => {
        aSelectRef.value?.$el?.nextElementSibling
          ?.querySelector('.arco-select-view-input')
          ?.focus();
      });
    }
  };
  // 默认值回显
  const fallback = () => {
    if (!activeOption.value?.[props.valueField]) return {};
    return {
      value: activeOption.value,
      label: activeOption.value?.[props.labelField],
    };
  };

  const routerTo = (routerName: string) => {
    if (props.addNoJump) {
      // 新增不跳转
      emit('addOption', searchKey.value);
      return;
    }
    switch (props.addRouterModel) {
      case 'open':
        openNewWindow(routerName);
        break;
      case 'push':
        routerPush(routerName);
        break;
      default:
        break;
    }
  };

  const handleSelectKeyDown = (e: any) => {
    if (props.isTable) {
      handleKeyDown(e);
    }
  };

  const handleSelectFooterKeyDown = (e: any) => {
    if (props.isTable) {
      handleKeyDown(e);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    handleInputKeyDown(e);
  };
  const handleInputKeyDown = (e: any) => {
    // 向a-select自带的input框派发一个keydown事件，走组件的快捷键
    aSelectRef.value?.$el?.nextElementSibling
      ?.querySelector('.arco-select-view-input')
      ?.dispatchEvent(new KeyboardEvent('keydown', e));
  };
  // 判断是否存在名为"arrow-icon"的插槽
  const hasArrowSlot = computed(() => {
    return !!instance?.slots?.['arrow-icon'];
  });
  // 判断是否存在名为"action"的插槽
  const hasActionSlot = computed(() => {
    return !!instance?.slots?.action;
  });
  const hasLabelSlot = computed(() => {
    return !!instance?.slots?.label;
  });
  const hasSwitchViewSlot = computed(() => {
    return !!instance?.slots?.['switch-view'];
  });
  const radioConfig: any = reactive({
    reserve: true,
    trigger: 'row',
    checkMethod: ({ row }: { row: Recordable }) => {
      return !(
        props.showDisabled &&
        (row[props.disabledField] === 0 || row.customDisabled)
      );
    },
  });
  const triggerPropsConfig = computed(() => {
    const config: Recordable = {
      preventFocus: false,
      autoFitPopupMinWidth: true,
    };
    if (props.showTab) {
      config['content-class'] = 'qs-select-trigger-content';
    }
    if (props.triggerProps) {
      Object.assign(config, props.triggerProps);
    }
    return config;
  });
  // 显示自定义搜索框
  const showCustomSearchInput = computed(() => {
    return !props.isTable && !props.hideHeader;
  });
  const basisTableRef = ref<any>();
  // vxe-table的ref
  const tableRef = computed(() => {
    return basisTableRef.value?.vxeTableRef;
  });
  const selectLoading = computed(() => {
    return loading.value || tableLoading.value;
  });
  const { handleKeyDown, handleRadioRow, clearSelectRow } = useTableKeyDown({
    tableData: selectOptions,
    tableRef,
    mode: 'radio',
    loading: selectLoading,
    pageParams: props.pageParams,
    pageChange,
  });
  // 处理鼠标事件，用于isTable为true时
  const handleMouse = (e: any, mouseType: 'mouseenter' | 'mouseleave') => {
    if (!props.isTable) return;
    handleSelectEllipsisTooltipMouse(e, mouseType);
  };
  watch(
    () => props.modelValue,
    (newVal) => {
      // 兼容全部选项的情况
      if (newVal || newVal === 0) {
        const id: any = newVal;
        const activeOptionVal =
          typeof newVal === 'string'
            ? `${activeOption.value?.[props.valueField]}`
            : activeOption.value?.[props.valueField];
        if (id !== activeOptionVal) {
          getOptionById(id);
        }
      } else if (activeOption?.value) {
        setActiveOption();
      }
    },
    { immediate: true }
  );
  defineExpose({
    setLoading,
    loading,
    searchKey,
    setPopupVisible,
  });
</script>

<script lang="ts">
  export default {
    name: 'QsSelect',
  };
</script>

<style lang="less" scoped>
  .select-options {
    :deep(.arco-select-option-content) {
      flex: 1;
    }
  }

  .tab-box {
    position: absolute;
    z-index: 1;
    width: 95px;
    height: 200px;

    :deep(.arco-tabs) {
      height: 100%;

      .arco-tabs-nav,
      .arco-tabs-nav-tab {
        width: 100%;
      }

      .arco-tabs-tab {
        padding: 0 5px;

        .arco-tabs-tab-title {
          width: 100%;
          white-space: pre-wrap;
          word-break: break-all;
        }
      }
    }
  }
</style>

<style lang="less">
  .max-w-670 {
    max-width: 670px;
  }

  .qs-select-trigger-content {
    .arco-select-dropdown-empty,
    .arco-select-dropdown-list,
    .arco-select-dropdown-loading {
      min-height: 200px;
      margin-left: 95px;
    }
  }
</style>
